import {
	useState,
	useEffect,
	useRef
} from "react";
import {
	Box,
	Typography,
	Alert,
	Chip,
	ToggleButton,
	ToggleButtonGroup,
	Grid,
	Paper,
	IconButton,
	Tooltip,
	CircularProgress,
	TableContainer,
	Table,
	TableRow,
	TableHead,
	TableBody,
	TableCell,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { alpha } from '@mui/material/styles';
import ErrorOutlineRoundedIcon from '@mui/icons-material/ErrorOutlineRounded';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import SectionTitle from '../../components/common/SectionTitle'
import DownloadRoundedIcon from '@mui/icons-material/DownloadRounded';
import CloudUploadRoundedIcon from '@mui/icons-material/CloudUploadRounded';
import CurrentClientContext from '../../providers/CurrentClientProvider'
import ClientService from '../../services/ClientService'
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import PlaylistAddCheckCircleOutlinedIcon from '@mui/icons-material/PlaylistAddCheckCircleOutlined';
import ClearOutlinedIcon from '@mui/icons-material/ClearOutlined';
import moment from 'moment'
import TipsContext from '../../providers/GlobalTip'
import EmptyIcon from '../../components/icons/EmptyIcon'
import AuthProvider from "../../providers/AuthProvider";
	
const jss = {
	changeLogItem: {
		display:'flex',
		p:1,
		border:'1px solid #ccc',
		borderRadius:1,
		mt:1,
		transition: 'all 0.3s',
		info: {
			flex:1,
			wordBreak:'break-all',
			cursor:'pointer',
		},
		name: {
			whiteSpace:"wrap",
			fontSize:13,
			fontWeight:'bold',
		},
		extra: {
			fontSize:12,
			mt:0.5,
		},
		checkIcon: {
			ml:1,
			cursor:'pointer',
		},
		":hover": {
			backgroundColor: '#f3f4f5',
		},
		'&.checked': {
			backgroundColor: '#dbcfe6',
			color:'primary.main',
			borderColor: 'primary.main'
		},
	},
	uploadDropZone: {
		p:0.5,
		borderRadius:1,
		border:'2px dashed transparent',
		transition: 'all 0.2s',
		'&.dragging': {
			borderColor: 'primary.main',
			backgroundColor: '#eee9f3',
		},
	},
	emptySelectLog: {
		textAlign:'center', 
		py:15, 
		color:'text.disabled', 
		fontSize:30
	},
	changeLogSection: {
		title: {
			fontSize:16,
			fontWeight:'bold',
			my:2
		},
		actionSection: {
			p:2,
			backgroundColor: '#eee9f3',
			display:'flex',
			alignItems:'center',
		},
	},
}

function ImportClass(props) {
	const fileInputRef = useRef()
	const selectedLogId = useRef()
	const dragDepth = useRef(0)
	const [dragging, setDragging] = useState(false)
	const [changeLogs, setChangeLogs] = useState()
	const [loading, setLoading] = useState(true)
	const [uploading, setUploading] = useState(false)
	const [processing, setProcessing] = useState(false)
	const [loadingSelect, setLoadingSelect] = useState(false)
	const currentClientContext = CurrentClientContext.useClient()
	const [choosedChangeLog, setChoosedChangeLog] = useState()
	const [targetChangeLog, setTargetChangeLog] = useState()
	const [changeLogSummary, setChangeLogSummary] = useState([])
	const tip_context = TipsContext.useMessage()
	const auth = AuthProvider.useAuth()
	const [isAdmin, setIsAdmin] = useState(false)
	// what processing the selected file would do: {errors: [], warnings: [{row, class_code, level, message}]}
	const [preview, setPreview] = useState()
	const [previewing, setPreviewing] = useState(false)
	const [processNotes, setProcessNotes] = useState([])
	const [noteFilter, setNoteFilter] = useState('all')

	useEffect(() => {
		document.title = 'Retail ORBIT: Client Class Import'
	}, [])

	useEffect(() => {
		if( auth.checkPermission('role-create') ) {
			setIsAdmin(true)
		}
	}, [auth.user])

	useEffect(() => {
		if( currentClientContext.client ) {
			loadChangeLogs()	
		}
	}, [currentClientContext.client])

	useEffect(() => {
		if( targetChangeLog ) {
			calculateChangeLogSummary()
		}
	}, [targetChangeLog])

	async function loadChangeLogs() {
		const _changeLog = await ClientService.getClientChangeLogs(currentClientContext.client.Id)
		setLoading(false)
		setChangeLogs(Array.isArray(_changeLog) ? _changeLog : [])
	}

	async function selectChangeLog(log) {
		selectedLogId.current = log.id
		setChoosedChangeLog(log)
		setLoadingSelect(true)
		setPreview(undefined)
		setPreviewing(false)
		setProcessNotes([])
		setNoteFilter('all')
		const _log = await ClientService.getClientChangeLogById(currentClientContext.client.Id, log.id)
		setTargetChangeLog(_log)
		setLoadingSelect(false)
		if( !_log.processed_date ) {
			runPreview(_log)
		}
	}

	// ask the server what processing would do (nothing is changed), so problems show before Process Changes
	async function runPreview(log) {
		setPreviewing(true)
		try {
			const result = await ClientService.previewClientChangeLog(currentClientContext.client.Id, log.id)
			if( selectedLogId.current===log.id ) {
				setPreview({errors: result?.errors || [], warnings: result?.warnings || []})
			}
		} catch(e) {
			if( selectedLogId.current===log.id ) {
				setPreview({errors: [], warnings: [{level: 'info', message: 'This file could not be checked in advance. You can still process it.'}]})
			}
		}
		if( selectedLogId.current===log.id ) {
			setPreviewing(false)
		}
	}

	async function deleteChangeLog(log) {
		await ClientService.deleteClientChangeLogById(currentClientContext.client.Id, log.id)
		const _changeLogs = []
		changeLogs.forEach(item => {
			if( item.id != log.id ) {
				_changeLogs.push(item)
			}
		})
		setChangeLogs(_changeLogs)
	}

	async function downloadTargetChangeLog() {
		ClientService.downloadClientChangeLog(currentClientContext.client.Id, targetChangeLog.id)
	}

	function calculateChangeLogSummary() {
		let _origin_summary = {}
		let _summary = []
		_origin_summary['_total'] = {
			Code: '',
			Name: "Total",
			Classes: 0,
			OTB: 0,
			PNOTB: 0,
			NOTB: 0,
			Other: 0,
			Delete: 0,
		}
		targetChangeLog.actions.forEach(_action => {
			if( _action.class_code ) {
				if( !_origin_summary.hasOwnProperty(_action.location_code) ) {
					_origin_summary[ _action.location_code ] = {
						Code: _action.location_code,
						Name: _action.location_name,
						Classes: 0,
						OTB: 0,
						PNOTB: 0,
						NOTB: 0,
						Other: 0,
						Delete: 0,
					}
				}
				if( String(_action.action || '').trim().toUpperCase()==='D' ) {
					// deleted classes are counted on their own, whatever service type the row carries
					_origin_summary[ _action.location_code ][ 'Delete' ]++;
					_origin_summary[ '_total' ][ 'Delete' ]++;
				} else if( _action.service_type=='Child' || !['OTB', 'PNOTB', 'NOTB'].includes(_action.service_type) ) {
					_origin_summary[ _action.location_code ][ 'Other' ]++;
					_origin_summary[ '_total' ][ 'Other' ]++;
				} else {
					_origin_summary[ _action.location_code ][ _action.service_type ]++;
					_origin_summary[ '_total' ][ _action.service_type ]++;	
				}
				_origin_summary[ _action.location_code ][ 'Classes' ]++;
				_origin_summary[ '_total' ][ 'Classes' ]++;
			}
		})
		_summary.push(_origin_summary._total)
		for(let i in _origin_summary) {
			if( i!='_total' ) {
				_summary.push(_origin_summary[i])
			}
		}
		setChangeLogSummary(_summary)
	}

	function chooseFile() {
		fileInputRef.current.click()
	}

	async function uploadFile(file) {
		if( !file ) {
			return
		}
		if( !/\.(xlsx|xls)$/i.test(file.name) ) {
			tip_context.show('Please choose an Excel file (.xlsx or .xls)')
			return
		}
		try {
			setUploading(true)
			const new_change_log = await ClientService.uploadClassData(currentClientContext.client.Id, file)
			setChangeLogs(prev => [new_change_log].concat(prev || []))
			// open the new file straight away so Process Changes is one click away
			await selectChangeLog(new_change_log)
		} catch(e) {
			tip_context.show(e.response?.data.message)
		}
		setUploading(false)
	}

	function uploadChangeLog(e) {
		const file = e.target.files[0]
		// clear the input so choosing the same file again still fires onChange
		e.target.value = ''
		uploadFile(file)
	}

	function isFileDrag(e) {
		return Array.from(e.dataTransfer?.types || []).includes('Files')
	}

	function dragEnter(e) {
		if( !isFileDrag(e) ) {
			return
		}
		e.preventDefault()
		dragDepth.current++
		setDragging(true)
	}

	function dragOver(e) {
		if( !isFileDrag(e) ) {
			return
		}
		e.preventDefault()
		e.dataTransfer.dropEffect = 'copy'
	}

	function dragLeave(e) {
		e.preventDefault()
		dragDepth.current = Math.max(0, dragDepth.current - 1)
		if( dragDepth.current===0 ) {
			setDragging(false)
		}
	}

	function dropFile(e) {
		e.preventDefault()
		dragDepth.current = 0
		setDragging(false)
		if( uploading ) {
			return
		}
		const files = e.dataTransfer.files
		if( files.length>1 ) {
			tip_context.show('Please drop one file at a time')
			return
		}
		uploadFile(files[0])
	}

	async function startProcessing() {
		try {
			setProcessing(true)
			const _change_log = await ClientService.processClientChangeLog(currentClientContext.client.Id, targetChangeLog.id)
			setTargetChangeLog(_change_log)
			setPreview(undefined)
			setProcessNotes(_change_log.warnings || [])
			// keep the file list in step so the processed check mark shows without a reload
			setChangeLogs(prev => (prev || []).map(item => item.id === _change_log.id ? {...item, processed_date: _change_log.processed_date} : item))
		} catch(e) {
			tip_context.show(e.response?.data.message)
		}
		setProcessing(false)
	}

	// Errors stop the file, warnings need a look first, notes are for information. Each gets its own segment.
	function renderImportNotes(errors, notes, processed) {
		const items = notes || []
		const segments = [
			{
				key: 'error',
				label: 'Errors',
				icon: ErrorOutlineRoundedIcon,
				color: 'error',
				help: 'These stop the file from being processed. Fix the file and upload it again.',
				// the server words errors as "Row 5: ..." or "Row 5, class 100: ...": show the row and class as chips like the warnings
				rows: (errors || []).map(message => {
					const match = /^Row (\d+)(?:, class ([^:]+))?: (.*)$/s.exec(message)
					return match ? {row: match[1], class_code: match[2], message: match[3]} : {message}
				}),
			},
			{
				key: 'warning',
				label: 'Warnings',
				icon: WarningAmberRoundedIcon,
				color: 'warning',
				help: processed ? 'Processed, but these need your attention.' : 'The file can be processed, but check these first.',
				rows: items.filter(item => item.level==='warning'),
			},
			{
				key: 'info',
				label: 'Notes',
				icon: InfoOutlinedIcon,
				color: 'info',
				help: 'For your information.',
				rows: items.filter(item => item.level!=='warning'),
			},
		].filter(segment => segment.rows.length>0)

		if( segments.length===0 ) {
			return null
		}

		const shown = noteFilter==='all' ? segments : segments.filter(segment => segment.key===noteFilter)

		// notes of one kind with a plural wording collapse into a single line listing the classes
		const groupRows = (rows) => {
			const groups = {}
			const out = []
			rows.forEach(item => {
				if( !item.kind || !item.group_message ) {
					out.push(item)
					return
				}
				if( !groups[item.kind] ) {
					groups[item.kind] = {kind: item.kind, group_message: item.group_message, items: []}
					out.push(groups[item.kind])
				}
				groups[item.kind].items.push(item)
			})
			return out.map(entry => {
				if( !entry.items ) {
					return entry
				}
				if( entry.items.length===1 ) {
					return entry.items[0]
				}
				const label = item => item.location_code ? `${item.class_code} (loc ${item.location_code})` : item.class_code
				return {
					group: true,
					count: entry.items.length,
					codes: entry.items.map(label).join(', '),
					message: entry.group_message,
				}
			})
		}

		return <Box>
			{segments.length>1 &&
				<ToggleButtonGroup size="small" exclusive value={noteFilter} onChange={(e, value) => value && setNoteFilter(value)} sx={{mb:1}}>
					<ToggleButton value="all" sx={{textTransform:'none'}}>All ({segments.reduce((sum, segment) => sum + segment.rows.length, 0)})</ToggleButton>
					{segments.map(segment => <ToggleButton key={`filter_${segment.key}`} value={segment.key} sx={{textTransform:'none'}}>
						{segment.label} ({segment.rows.length})
					</ToggleButton>)}
				</ToggleButtonGroup>
			}
			<Box sx={{display:'flex', flexDirection:'column', gap:1.5}}>
				{shown.map(segment => {
					const Icon = segment.icon
					return <Box
						key={`segment_${segment.key}`}
						sx={theme => ({
							borderLeft: `4px solid ${theme.palette[segment.color].main}`,
							backgroundColor: alpha(theme.palette[segment.color].main, 0.06),
							borderRadius: 1,
							p: 1.5,
						})}
					>
						<Box sx={{display:'flex', alignItems:'center', gap:1, mb:1}}>
							<Icon color={segment.color} fontSize="small" />
							<Typography sx={{fontWeight:'bold', fontSize:14}}>{segment.label} ({segment.rows.length})</Typography>
							<Typography variant="body2" color="text.secondary">{segment.help}</Typography>
						</Box>
						<Box sx={{display:'flex', flexDirection:'column', gap:1}}>
							{groupRows(segment.rows).map((item, i) => <Box key={`${segment.key}_${i}`} sx={{display:'flex', alignItems:'flex-start', gap:1}}>
								{item.group &&
									<Box sx={{flexShrink:0, minWidth:140}}>
										<Chip size="small" color={segment.color} label={`${item.count} classes`} />
									</Box>
								}
								{item.group &&
									<Box>
										<Typography variant="body2">{item.message}</Typography>
										<Typography variant="body2" sx={{color:'text.secondary', wordBreak:'break-word'}}>{item.codes}</Typography>
									</Box>
								}
								{!item.group && (item.row || item.class_code) &&
									<Box sx={{display:'flex', gap:0.5, flexShrink:0, minWidth:140}}>
										{item.row ? <Chip size="small" variant="outlined" color={segment.color} label={`Row ${item.row}`} /> : null}
										{item.class_code ? <Chip size="small" variant="outlined" color={segment.color} label={`Class ${item.class_code}`} /> : null}
									</Box>
								}
								{!item.group && <Typography variant="body2">{item.message}</Typography>}
							</Box>)}
						</Box>
					</Box>
				})}
			</Box>
		</Box>
	}

	const hasPreviewErrors = (preview?.errors || []).length>0
	const showNotes = previewing || hasPreviewErrors || (preview?.warnings || []).length>0 || processNotes.length>0

	return <Box sx={{py:2}}>
		<Typography variant="h5">Client Import</Typography>
		<Grid container spacing={2} sx={{mt:0}}>
			<Grid item xs={12} sm={3}>
				<Paper elevation={2}>
					<SectionTitle 
						label="Import Classes"
						rightComponent={
							<Tooltip title="Download Setup Package">
								<IconButton component="a" href="/templates/client_setup_package.zip" target="_blank">
									<DownloadRoundedIcon />
								</IconButton>
							</Tooltip>
						}
					/>
					<Box sx={{p:1.5}}>
						<Box 
							component="input" 
							type='file' 
							name="data" 
							sx={{display:'none'}} 
							ref={fileInputRef}
							accept="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
							onChange={uploadChangeLog}
						></Box>
						{isAdmin ?
							<Box
									sx={jss.uploadDropZone}
									className={dragging ? 'dragging' : ''}
									onDragEnter={dragEnter}
									onDragOver={dragOver}
									onDragLeave={dragLeave}
									onDrop={dropFile}
								>
									<LoadingButton loading={uploading} variant="contained" fullWidth startIcon={<CloudUploadRoundedIcon />} onClick={chooseFile}>{dragging ? 'Drop to upload' : 'Upload File'}</LoadingButton>
								</Box>
						: null}
						<Typography variant="body2" sx={{mt:3, mb:1}}>Class Change Files</Typography>
						{changeLogs?.length>0 && changeLogs.map(_log => {
							const addedCount = typeof _log.actions_count !== 'undefined'
								? _log.actions_count
								: (Array.isArray(_log.actions) ? _log.actions.length : 0)
							return <Box key={`change_log_${_log.id}`} sx={jss.changeLogItem} className={choosedChangeLog?.id==_log.id ? 'checked' : ''} >
								<Box sx={jss.changeLogItem.info} onClick={e => selectChangeLog(_log)}>
									<Box sx={{...jss.changeLogItem.name, display:'flex', alignItems:'center', gap:0.5, flexWrap:'wrap'}}>
										{_log.file_name==='api' && <Chip size="small" color="secondary" variant="outlined" label="API" sx={{height:18, fontSize:10}} />}
										<span>{_log.name}</span>
									</Box>
									<Box sx={jss.changeLogItem.extra}>Added: {addedCount}</Box>
								</Box>
								{_log.processed_date && 
									<CheckRoundedIcon sx={jss.changeLogItem.checkIcon}/>
								}
								{!_log.processed_date && 
									<ClearOutlinedIcon sx={jss.changeLogItem.checkIcon} onClick={e => deleteChangeLog(_log)}/>
								}
							</Box>
						})}
						{loading && <Box sx={{textAlign:'center',pt:2}}>
							<CircularProgress color="primary" />
						</Box>
						}
						{changeLogs?.length===0 && !loading && 
							<Box sx={{py:2,textAlign:'center',color:'text.disabled'}}><EmptyIcon sx={{fontSize:60}}/></Box>
						}
					</Box>
				</Paper>
			</Grid>
			<Grid item xs={12} sm={9}>
				<Paper elevation={2}>
					{(!targetChangeLog && !loadingSelect) && 
						<Box sx={jss.emptySelectLog}>
							Please select a class change file
						</Box>
					}
					{loadingSelect && 
						<Box sx={jss.emptySelectLog}>
							<CircularProgress color="primary" />
						</Box>
					}
					{targetChangeLog && 
						<Box sx={jss.changeLogSection}>
							<SectionTitle 
								label='Class Change'
								subLabel={targetChangeLog.processed_date ? "File Processed Successfully" : ""}
								rightComponent={
									<IconButton onClick={downloadTargetChangeLog}>
										<DownloadRoundedIcon />
									</IconButton>
								}
							/>
							<Box sx={{p:2}}>
								<Box sx={jss.changeLogSection.title}>Change Summary</Box>
								<TableContainer>
									<Table size="small">
										<TableHead>
											<TableRow>
												<TableCell>Code</TableCell>
												<TableCell>Name</TableCell>
												<TableCell>Classes</TableCell>
												<TableCell>OTB</TableCell>
												<TableCell>PNOTB</TableCell>
												<TableCell>NOTB</TableCell>
												<TableCell>Other</TableCell>
													<TableCell>Delete</TableCell>
											</TableRow>
										</TableHead>
										<TableBody>
											{changeLogSummary.map((item, ) => {
												return <TableRow key={`summary_item_${item.Name}`}>
													<TableCell><b>{item.Code}</b></TableCell>
													<TableCell><b>{item.Name}</b></TableCell>
													<TableCell>{item.Classes}</TableCell>
													<TableCell>{item.OTB}</TableCell>
													<TableCell>{item.PNOTB}</TableCell>
													<TableCell>{item.NOTB}</TableCell>
													<TableCell>{item.Other}</TableCell>
														<TableCell>{item.Delete}</TableCell>
												</TableRow>	
											})}
										</TableBody>
									</Table>
								</TableContainer>
							</Box>
							{showNotes &&
								<Box sx={{px:2, pb:2}}>
									{previewing && <Alert severity="info" icon={<CircularProgress size={18} />}>Checking this file...</Alert>}
									{!previewing && renderImportNotes(preview?.errors, [...(preview?.warnings || []), ...processNotes], processNotes.length>0)}
								</Box>
							}
							<Box sx={jss.changeLogSection.actionSection}>
								<LoadingButton variant="contained" loading={processing} disabled={previewing || hasPreviewErrors || !!targetChangeLog.processed_date} onClick={startProcessing} startIcon={<PlaylistAddCheckCircleOutlinedIcon />}>Process Changes</LoadingButton>
								{targetChangeLog.processed_date &&
									<Box sx={{ml:2}}><b>File Processed Successfully:</b> {moment(targetChangeLog.processed_date).format('MM/DD/YYYY')}</Box>
								}
							</Box>
							<Box sx={{p:2}}>
								<TableContainer>
									<Table size="small">
										<TableHead>
											<TableRow>
												<TableCell>Action</TableCell>
												<TableCell>Location Code</TableCell>
												<TableCell>Location Name</TableCell>
												<TableCell>Class Code</TableCell>
												<TableCell>Class Name</TableCell>
												<TableCell>Parent Code</TableCell>
												<TableCell>Parent Location</TableCell>
												<TableCell>Service</TableCell>
												<TableCell>Category</TableCell>
											</TableRow>
										</TableHead>
										<TableBody>
										{targetChangeLog.actions.map(_action => {
											return <TableRow key={`action_item_${_action.id}`}>
												<TableCell>{_action.action}</TableCell>
												<TableCell>{_action.location_code}</TableCell>
												<TableCell>{_action.location_name}</TableCell>
												<TableCell>{_action.class_code}</TableCell>
												<TableCell>{_action.class_name}</TableCell>
												<TableCell>{_action.parent_class_code}</TableCell>
												<TableCell>{_action.parent_location_code}</TableCell>
												<TableCell>{_action.service_type}</TableCell>
												<TableCell>{_action.class_category}</TableCell>
											</TableRow>
										})}
										</TableBody>
									</Table>
								</TableContainer>
							</Box>
						</Box>
					}
				</Paper>
			</Grid>
		</Grid>
	</Box>
}
export default ImportClass